import { ArrowBackIosNewRounded, Close, Undo } from '@mui/icons-material';
import { Accordion, AccordionActions, AccordionDetails, AccordionSummary, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ReportTask } from '@nx-smartmonkey/shared/domain';
import { RoutalButton as Button, HSpacer, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { motion } from 'framer-motion';
import { createRef, useEffect, useState } from 'react';

import SuccessIcon from '../../../../../../../../assets/svg/CheckIcon';
import CrossIcon from '../../../../../../../../assets/svg/CrossIcon';
import { TrashIcon } from '../../../../../../../../assets/svg/TrashIcon';
import { STOP_TASK_STATUS, StopTaskStatus } from '../../../../../../../../constants/Stops';
import { StopTask } from '../../../../../../../../domain/stops/StopTask';
import { Text } from '../../../../../../../../react-components/Text';
import { useRetrieveDriverCredentials } from '../../../../../../hooks/useRetrieveDriverCredentials';
import { useRetrieveVehicle } from '../../../../../../hooks/useRetrieveVehicle';
import { TaskContent } from './TaskContent';

const useStyles = makeStyles({
  expandIconWrapper: {
    transform: ``,
    transition: `transform 0.2s ease-in-out`,
    '&$expanded': {
      transform: `rotate(-180deg) !important`,
    },
  },
  expanded: {},
  notexpandIconWrapper: {
    transform: `none !important `,
  },
  content: {
    margin: `12px 0px !important`,
  },
});

const styles = {
  taskElementButton: {
    flexGrow: `1`,
    borderRadius: `10px`,
    height: `48px`,
    padding: `0px`,
  },
  taskElementDraggable: {
    maxWidth: `100%`,
    display: `flex`,
    justifyContent: `center`,
    alignItems: `center`,
  },
  taskElementDraggableIcon: {
    height: `22px`,
    width: `22px`,
    fill: RoutalPalette.white,
    transition: `all 0.2s ease-in-out`,
  },
};

interface TaskElementProps {
  task: StopTask;
  reportTask: ReportTask;
  isExpanded: boolean;
  isTemporal: boolean;
  updateTask: (task: StopTask, newStatus: StopTaskStatus) => void;
  updateExpanded: (expanded: boolean) => void;
  deleteTask?: (task: StopTask) => void;
}

export const TaskElement = ({
  task,
  reportTask,
  isExpanded,
  isTemporal,
  updateTask,
  updateExpanded,
  deleteTask,
}: TaskElementProps) => {
  const { data: vehicleData } = useRetrieveVehicle();
  const { data: credentials } = useRetrieveDriverCredentials();
  const [widthConfirm, setWidthConfirm] = useState(0);
  const [widthCancel, setWidthCancel] = useState(0);

  const confirmRef = createRef<HTMLDivElement>();
  const cancelRef = createRef<HTMLDivElement>();

  const MAX_DRAG_WIDTH = window.innerWidth * 0.2;
  const THRESHOLD_ACTION = 30;

  useEffect(() => {
    // Do nothing, just react to the change. Without this, the element don't change when the father component change the expanded state
  }, [isExpanded]);

  const classes = useStyles();

  if (!credentials || !vehicleData) {
    return null;
  }

  // This function will be called each time the user drags the task element and the state of the drag changes
  const dragTrigger = (event: any, info: any) => {
    // If there is not a reference to the lateral confirm or cancel elements, we do nothing
    if (!cancelRef.current || !confirmRef.current) {
      return;
    }

    // TODO. Check this when the user drags the element to the opposite side when the element is already dragged
    // We check the following conditions:
    // 1. The offset of the dragging is different than zero. This helps us define to which side the user is dragging the element
    // 2. The condition of the opposite element being zero. This means that the user is not dragging the opposite element
    // 3. The offset is less than the maximum width of the drag. This is to avoid the user dragging the element too much
    if (info.offset.x > 0 && cancelRef.current.clientWidth === 0 && info.offset.x <= MAX_DRAG_WIDTH) {
      setWidthConfirm(info.offset.x);
    } else if (info.offset.x < 0 && confirmRef.current.clientWidth === 0 && Math.abs(info.offset.x) <= MAX_DRAG_WIDTH) {
      setWidthCancel(Math.abs(info.offset.x));
    }
  };

  // This is calles once the user releases the drag
  const dragEnd = () => {
    // In case the user have dragged the element more distance than the threshold, we update the task status
    if (widthConfirm > THRESHOLD_ACTION) {
      updateTask(task, STOP_TASK_STATUS.completed as StopTaskStatus);
    }
    if (widthCancel > THRESHOLD_ACTION) {
      updateTask(task, STOP_TASK_STATUS.canceled as StopTaskStatus);
    }

    // We reset the width of the confirm and cancel elements
    setWidthConfirm(0);
    setWidthCancel(0);
  };

  return (
    <Grid style={{ display: `flex`, flexDirection: `row`, position: `relative`, minHeight: `fit-content` }}>
      <Grid
        ref={confirmRef}
        style={{
          ...styles.taskElementDraggable,
          position: `absolute`,
          left: 0,
          top: 0,
          bottom: 0,
          width: widthConfirm,
          transition: widthConfirm ? `none` : `width 0.2s ease-in-out`,
          backgroundColor: RoutalPalette.success.medium,
        }}
      >
        <SuccessIcon
          style={{ ...styles.taskElementDraggableIcon, opacity: widthConfirm > THRESHOLD_ACTION ? `100%` : `0%` }}
        />
      </Grid>
      <motion.div
        drag="x"
        dragSnapToOrigin={true}
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0}
        onDrag={dragTrigger}
        onDragEnd={dragEnd}
        style={{
          width: `100%`,
          position: `relative`,
          left: widthConfirm > 0 ? widthConfirm : widthCancel > 0 ? widthCancel * -1 : 0,
          transition: widthConfirm + widthCancel > 0 ? `none` : `left 0.2s ease-in-out`,
        }}
      >
        <Accordion
          key={task.id}
          style={{
            width: `100%`,
            display: `flex`,
            flexDirection: `column`,
            justifyContent: `space-between`,
            borderBottom: `1px solid ${RoutalPalette.grey4}`,
            boxShadow: `none`,
          }}
          expanded={isExpanded}
          onChange={(event, expanded) => {
            updateExpanded(expanded);
          }}
        >
          <AccordionSummary
            expandIcon={
              <>
                {reportTask?.status === (STOP_TASK_STATUS.pending as StopTaskStatus) ? (
                  <ArrowBackIosNewRounded
                    style={{
                      fill: RoutalPalette.text,
                      opacity: 0.5,
                      transform: `rotate(90deg)`,
                    }}
                  />
                ) : reportTask?.status === (STOP_TASK_STATUS.completed as StopTaskStatus) ? (
                  <SuccessIcon
                    style={{
                      fill: RoutalPalette.success.medium,
                      opacity: 0.5,
                      width: `24px`,
                      height: `24px`,
                    }}
                  />
                ) : (
                  <CrossIcon
                    style={{ fill: RoutalPalette.error.medium, opacity: 0.5, width: `20px`, height: `20px` }}
                  />
                )}
              </>
            }
            classes={{
              expandIconWrapper:
                reportTask?.status === (STOP_TASK_STATUS.pending as StopTaskStatus)
                  ? classes.expandIconWrapper
                  : classes.notexpandIconWrapper,
              expanded: classes.expanded,
              content: classes.content,
            }}
            style={{
              minHeight: `60px`,
              flexDirection: `row`,
              justifyContent: `space-between`,
              borderBottom: `1px solid ${RoutalPalette.grey4}`,
              transition: `height 0.2s ease-in-out`,
            }}
          >
            <Grid
              style={{
                display: `flex`,
                flexDirection: `row`,
                flexGrow: 1,
                justifyContent: `space-between`,
                alignItems: `center`,
              }}
            >
              {task.label ? (
                <Text
                  style={{
                    fontSize: `16px`,
                    color:
                      reportTask?.status !== (STOP_TASK_STATUS.pending as StopTaskStatus)
                        ? RoutalPalette.secondary00
                        : undefined,

                    lineHeight: `1.2em`,
                    display: isExpanded ? `block` : `-webkit-box`,
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: `vertical`,
                    overflow: `hidden`,
                  }}
                >
                  {task.label}
                </Text>
              ) : (
                <Text
                  style={{
                    fontSize: `16px`,
                    color:
                      reportTask?.status !== (STOP_TASK_STATUS.pending as StopTaskStatus)
                        ? RoutalPalette.secondary00
                        : RoutalPalette.neutral20,
                    fontStyle: `italic`,
                    width: `100%`,
                    lineHeight: `1.2em`,
                    display: isExpanded ? `block` : `-webkit-box`,
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: `vertical`,
                    overflow: `hidden`,
                  }}
                  intlId="driver.task.label.empty"
                />
              )}
              {isTemporal && (
                <Grid
                  style={{
                    marginRight: 10,
                    borderRadius: 100,
                    backgroundColor: RoutalPalette.primary40,
                    height: `8px`,
                    width: `8px`,
                  }}
                />
              )}
            </Grid>
          </AccordionSummary>

          <AccordionDetails>
            <TaskContent task={task} fields={vehicleData.project.fields.task_fields} />
            {deleteTask && credentials.test(`can_update_stop_tasks`) && (
              <>
                <VSpacer small />
                <Grid style={{ display: `flex`, justifyContent: `flex-end` }}>
                  <Button variant="text" onClick={() => deleteTask(task)} style={{ padding: 5 }}>
                    <TrashIcon style={{ width: `20px`, height: `20px`, color: `${RoutalPalette.error.medium}` }} />
                    <Text
                      intlId="driver.task.delete"
                      style={{ cursor: `pointer`, fontSize: `12px`, color: `${RoutalPalette.error.medium}` }}
                    />
                  </Button>
                </Grid>
              </>
            )}
          </AccordionDetails>

          <AccordionActions
            style={{ display: `flex`, flexDirection: `row`, alignItems: `center`, justifyContent: `space-around` }}
          >
            <>
              {reportTask?.status === (STOP_TASK_STATUS.pending as StopTaskStatus) ? (
                <Grid container flexDirection={`row`}>
                  <HSpacer small />
                  <Button
                    style={{
                      ...styles.taskElementButton,
                      backgroundColor: `transparent`,
                      border: `1px solid ${RoutalPalette.error.medium}`,
                      textTransform: `none`,
                    }}
                    onClick={() => updateTask(task, STOP_TASK_STATUS.canceled as StopTaskStatus)}
                  >
                    <Close style={{ height: `22px`, width: `22px`, fill: RoutalPalette.error.medium }} />
                    <HSpacer small />
                    <Text
                      variant="body-medium"
                      intlId="driver.stop.entry.cancel"
                      style={{ color: RoutalPalette.error.medium }}
                    />
                  </Button>
                  <HSpacer small />
                  <Button
                    style={{
                      ...styles.taskElementButton,
                      backgroundColor: RoutalPalette.success.medium,
                      border: `1px solid ${RoutalPalette.success.medium}`,
                      textTransform: `none`,
                    }}
                    onClick={() => updateTask(task, STOP_TASK_STATUS.completed as StopTaskStatus)}
                  >
                    <SuccessIcon style={{ height: `22px`, width: `22px`, fill: RoutalPalette.white }} />
                    <HSpacer small />
                    <Text
                      variant="body-medium"
                      intlId="driver.stop.entry.complete"
                      style={{ color: RoutalPalette.white }}
                    />
                  </Button>
                  <HSpacer small />
                </Grid>
              ) : (
                <Button variant="text" onClick={() => updateTask(task, STOP_TASK_STATUS.pending as StopTaskStatus)}>
                  <Undo style={{ fill: RoutalPalette.primary40, height: `22px`, cursor: `pointer` }} />
                  <HSpacer small />
                  <Text
                    intlId="driver.stop.report.tasks.undo"
                    style={{ color: RoutalPalette.primary40, cursor: `pointer` }}
                  />
                </Button>
              )}
            </>
          </AccordionActions>
        </Accordion>
      </motion.div>
      <Grid
        ref={cancelRef}
        style={{
          ...styles.taskElementDraggable,
          position: `absolute`,
          right: 0,
          top: 0,
          bottom: 0,
          width: widthCancel,
          transition: widthCancel ? `none` : `width 0.2s ease-in-out`,
          backgroundColor: RoutalPalette.error.medium,
        }}
      >
        <CrossIcon
          style={{ ...styles.taskElementDraggableIcon, opacity: widthCancel > THRESHOLD_ACTION ? `100%` : `0%` }}
        />
      </Grid>
    </Grid>
  );
};
